import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePhiSearchTerm } from '../../lib/phiSearch';
import {
  Wallet, Stethoscope, CheckCircle2, Clock, Package,
  Inbox, SearchX, FilterX, RotateCw, FileSpreadsheet, ChevronDown, ChevronLeft,
} from 'lucide-react';
import { C, CT } from '@parsina/framework/clinical/src/constants/theme';

import {
  DateRangeFilter, rangeLabelFor, useToast,
  PageHeader, KpiCard, EmptyState, SearchInput, Select, Button, SkeletonRow,
} from '@parsina/framework';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { accountingLineLabel } from '../../lib/ledgerVocabulary';
import SegmentedControl from '../../components/common/SegmentedControl';
import Pager from '../../components/common/Pager';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { PAGE_SIZE } from '../../constants/pagination';
import {
  useAccounting, useMyClinics, fetchAllReportLines,
  presetRange, customRange, translatedPresets,
} from './useAccounting';
import {
  soft, ITEM_TYPE_META, TABS, TYPE_OPTIONS, DEFAULTS,
  fmtDate, exportLinesCsv, yourShareText,
} from './Accounting.shared';
import {
  groupByCurrency, scaleMax, reportTotals, displayCurrencyFor,
} from '../../lib/clinicMoney';

// This file is the DESKTOP surface only (DESIGN-CONCEPT-MOBILE v2 §1.1): the
// phone renders the independent `Accounting.mobile.jsx`, so there is no
// responsive fork here. Non-visual truth (item meta, tabs, CSV, date format,
// defaults) lives in `Accounting.shared` and is imported by both surfaces.

// ── The one column/label definition (§5.1: table renders from it) ──────────────
const COLUMNS = (t) => ([
  { id: 'date',        label: t('common.labels.date') },
  { id: 'clinic',      label: t('common.labels.clinic') },
  { id: 'description', label: t('accounting.serviceDescription') },
  { id: 'type',        label: t('common.labels.type') },
  { id: 'gross',       label: t('accounting.totalAmount') },
  { id: 'doctorShare', label: t('accounting.yourShare') },
  { id: 'settlement',  label: t('accounting.settlement') },
]);

// ── Rail cards ─────────────────────────────────────────────────────────────────

// Rows come one per (clinic, currency), IRR first. Each currency is its own chart:
// a bar is scaled against the largest bar IN ITS CURRENCY, never across — with no
// exchange rate, a dirham bar next to a toman bar would compare nothing. When
// more than one currency is present each group is headed by its currency's name.
const CurrencyHeading = ({ currency }) => {
  const { moneyUnit } = useTranslation();
  return (
    <div className="text-[0.6875rem] font-bold text-muted-foreground" data-testid={`accounting-currency-heading-${currency}`}>
      {moneyUnit(currency)}
    </div>
  );
};

const ClinicBreakdown = ({ clinics }) => {
  const { t, formatMoney, formatNumber, moneyUnit } = useTranslation();
  if (!clinics.length) return null;
  const groups = groupByCurrency(clinics);
  const mixed = groups.length > 1;

  return (
    <div className="bg-white rounded-2xl shadow-card p-6" data-testid="accounting-breakdown-clinic">
      <div className="text-[0.9375rem] font-extrabold text-foreground mb-1">{t('accounting.byClinic')}</div>
      <div className="text-xs text-muted-foreground mb-[1.125rem]">{t('accounting.shareByClinic')}</div>

      <div className="flex flex-col gap-4">
        {groups.map(({ currency, rows }) => {
          const max = scaleMax(rows);
          const unit = moneyUnit(currency, 'short');
          return (
            <React.Fragment key={currency}>
              {mixed && <CurrencyHeading currency={currency} />}
              {rows.map((c) => {
                const pct = (c.grossTotal / max) * 100;
                return (
                  <div key={c.clinicId || 'none'}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="flex-1 text-[0.8125rem] font-bold text-foreground">
                        {c.clinicName || (c.clinicId ? t('accounting.unknownClinic') : t('accounting.noClinic'))}
                      </span>
                      <span className="text-[0.8125rem] font-extrabold text-clinic-teal">
                        {formatMoney(c.doctorShareTotal, { currency })}
                        <span className="text-[0.625rem] font-semibold text-muted-foreground me-0.5">{unit}</span>
                      </span>
                    </div>

                    <div className="h-1.5 bg-background rounded overflow-hidden mb-1.5">
                      <div
                        className="h-full bg-clinic-teal rounded transition-[width] duration-500"
                        style={{ width: `${pct}%` }}
                        data-testid={`accounting-clinic-bar-${c.clinicId || 'none'}-${currency}`}
                      />
                    </div>

                    <div className="flex gap-2.5 text-[0.6875rem] text-muted-foreground flex-wrap">
                      <span>{t('accounting.totalLabel')} {formatMoney(c.grossTotal, { currency })} {unit}</span>
                      <span>{t('accounting.collectedLabel')} {formatMoney(c.collectedDoctorShare, { currency })} {unit}</span>
                      <span>
                        {formatNumber(c.visitCount)} {t('patient.visit')}
                        {c.medicalServiceCount > 0 && ` · ${t('accounting.paraclinicalCount', { count: c.medicalServiceCount })}`}
                        {c.productCount > 0 && ` · ${t('accounting.goodsCount', { count: c.productCount })}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Per-type roll-up computed by the BFF over the SAME whole-window filtered set as the
// KPI cards — not the visible page — so «جمع کل کارکرد» always equals the column of
// gross figures below it, whatever page the table is on.
// One row per (type, currency), grouped by currency like the clinic card above.
const TypeBreakdown = ({ types }) => {
  const { t, formatMoney, formatNumber, moneyUnit } = useTranslation();
  if (!types.length) return null;
  const groups = groupByCurrency(types);
  const mixed = groups.length > 1;

  return (
    <div className="bg-white rounded-2xl shadow-card p-6" data-testid="accounting-breakdown-type">
      <div className="text-[0.9375rem] font-extrabold text-foreground mb-1">{t('accounting.byServiceType')}</div>
      <div className="text-xs text-muted-foreground mb-4">{t('accounting.totalForRange')}</div>

      <div className="flex flex-col gap-3">
        {groups.map(({ currency, rows }) => (
          <React.Fragment key={currency}>
            {mixed && <CurrencyHeading currency={currency} />}
            {rows.map((line) => {
              const m = ITEM_TYPE_META(t)[line.itemType] || { label: line.itemType, Icon: Package, color: C.textSecondary };
              const Icon = m.Icon;
              return (
                <div key={line.itemType} className="flex items-center gap-2.5">
                  <span
                    className="w-[2.125rem] h-[2.125rem] rounded-[0.625rem] flex items-center justify-center shrink-0"
                    style={{ background: soft(m.color, 0.1), color: m.color }}
                  >
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <div className="text-[0.8125rem] font-bold text-foreground">{m.label}</div>
                    <div className="text-[0.6875rem] text-muted-foreground">{formatNumber(line.count)} {t('common.units.item')}</div>
                  </span>
                  <span className="text-start">
                    <div className="text-[0.8125rem] font-extrabold" style={{ color: m.color }}>
                      {formatMoney(line.grossTotal, { currency })}
                      <span className="text-[0.625rem] font-semibold text-muted-foreground me-0.5">{moneyUnit(currency, 'short')}</span>
                    </div>
                    <div className="text-[0.6875rem] text-muted-foreground">
                      {line.doctorShareTotal != null ? yourShareText(line.doctorShareTotal, currency, t, formatMoney, moneyUnit) : t('accounting.noSplitRule')}
                    </div>
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const TypeBadge = ({ itemType }) => {
  const { t } = useTranslation();
  const m = ITEM_TYPE_META(t)[itemType] || { label: itemType, Icon: Package, color: C.textSecondary };
  const Icon = m.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-2 py-[0.1875rem] text-[0.6875rem] font-bold whitespace-nowrap"
      style={{ background: soft(m.color, 0.1), color: m.color }}
    >
      <Icon size={12} aria-hidden="true" />{m.label}
    </span>
  );
};

// ── Rows ────────────────────────────────────────────────────────────────────────

// Desktop table row (§5.1 real <table> semantics).
const LineRow = ({ line }) => {
  const { t, formatDigits, formatDate, formatMoney, moneyUnit } = useTranslation();
  // Each line is in its own invoice's currency; a table can hold two.
  const currency = line.currency;
  const unit = moneyUnit(currency, 'short');
  return (
  <tr className="border-t border-border" data-testid={`accounting-row-${line.id}`}>
    <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(line.createdAt, formatDate)}</td>
    <td className="px-5 py-3 text-xs text-muted-foreground max-w-[10rem] truncate" title={line.clinicName || undefined}>
      {line.clinicName || (line.clinicId ? '—' : t('accounting.noClinic'))}
    </td>
    <td className="px-5 py-3 min-w-0">
      {/* `description` is written by the accounting service, in Persian —
          «ویزیت» for the visit line. Translated at the display edge; a clinic's
          own catalogue service name has no mapping and renders as itself. */}
      <div className="text-[0.8125rem] font-bold text-foreground max-w-[16rem] truncate" title={accountingLineLabel(line.description, t)}>
        {accountingLineLabel(line.description, t)}
        {line.quantity > 1 && (
          <span className="text-[0.6875rem] font-semibold text-muted-foreground me-1">×{formatDigits(line.quantity)}</span>
        )}
      </div>
      {line.patientName && <div className="text-[0.6875rem] text-muted-foreground mt-0.5">{line.patientName}</div>}
    </td>
    <td className="px-5 py-3"><TypeBadge itemType={line.itemType} /></td>
    <td className="px-5 py-3 text-[0.8125rem] font-bold text-foreground whitespace-nowrap">
      {formatMoney(line.grossAmount, { currency })}<span className="text-[0.625rem] text-muted-foreground me-0.5">{unit}</span>
    </td>
    <td className={`px-5 py-3 text-[0.8125rem] font-extrabold whitespace-nowrap ${line.doctorShare == null ? 'text-muted-foreground' : 'text-clinic-teal'}`}>
      {line.doctorShare == null ? '—' : (
        <>{formatMoney(line.doctorShare, { currency })}<span className="text-[0.625rem] text-muted-foreground me-0.5">{unit}</span></>
      )}
    </td>
    <td className="px-5 py-3"><StatusChip status={line.settlementStatus} /></td>
  </tr>
);
                };

// A settlement/table title bar reused by both the doctor's rows and the staff rows.
const TableHead = () => {
  const { t } = useTranslation();
  return (
  <thead>
    <tr className="bg-background border-b border-border">
      {COLUMNS(t).map((c) => (
        <th key={c.id} className="sticky top-0 z-10 bg-background px-5 py-2.5 text-[0.6875rem] font-bold text-muted-foreground text-start whitespace-nowrap">
          {c.label}
        </th>
      ))}
    </tr>
  </thead>
);
                  };

// ── KPIs ──────────────────────────────────────────────────────────────────────

// One currency's four KPI cards. `group` is one entry of `reportTotals` — every
// figure in it, `outstanding` included, is minor units of `group.currency`. The
// testids carry a currency suffix only when there is more than one group.
const KpiGroup = ({ group, skeleton, suffix = '' }) => {
  const { t, formatMoney, moneyUnit } = useTranslation();
  const { currency } = group;
  const unit = moneyUnit(currency, 'short');
  return (
    <>
      <div data-testid={`accounting-kpi-gross${suffix}`}>
        <KpiCard
          label={t('accounting.grandTotal')} value={formatMoney(group.grossTotal, { currency })} unit={unit}
          icon={<Wallet size={22} color={C.navy} aria-hidden="true" />}
          bg={soft(C.navy, 0.1)} accent={C.navy} loading={skeleton}
        />
      </div>
      <div data-testid={`accounting-kpi-share${suffix}`}>
        <KpiCard
          label={t('accounting.yourShareNet')} value={formatMoney(group.doctorShareTotal, { currency })} unit={unit}
          icon={<Stethoscope size={22} color={C.teal} aria-hidden="true" />}
          bg={C.tealLight} accent={C.teal} loading={skeleton}
        />
      </div>
      <div data-testid={`accounting-kpi-collected${suffix}`}>
        <KpiCard
          label={t('accounting.collected')} value={formatMoney(group.collectedDoctorShare, { currency })} unit={unit}
          icon={<CheckCircle2 size={22} color={C.green} aria-hidden="true" />}
          bg={C.greenLight} accent={C.green} loading={skeleton}
        />
      </div>
      <div data-testid={`accounting-kpi-outstanding${suffix}`}>
        <KpiCard
          label={t('accounting.awaitingCollection')} value={formatMoney(group.outstanding, { currency })} unit={unit}
          icon={<Clock size={22} color={CT.warnInk} aria-hidden="true" />}
          bg={C.amberLight} accent={CT.warnInk} loading={skeleton}
        />
      </div>
    </>
  );
};

// ── Screen ────────────────────────────────────────────────────────────────────
// `DEFAULTS` — the filter defaults (a param at its default is removed from the
// URL) — is shared with the mobile surface via `Accounting.shared`.

const AccountingWeb = () => {
  const { t, formatDate, formatNumber, moneyUnit } = useTranslation();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL → state (§3.2).
  const param    = (k) => searchParams.get(k) ?? DEFAULTS[k];
  const tab      = TABS(t).some(item => item.id === param('settle')) ? param('settle') : 'ALL';
  const clinicId = param('clinic');
  const itemType = param('type');
  // The search term can be a patient's name, so it is the one filter kept OUT
  // of the URL (§3.2) — tab session state instead (lib/phiSearch).
  const [query, setQuery] = usePhiSearchTerm('accounting');
  const preset   = param('preset');
  const custom   = { from: param('from'), to: param('to') };
  const page      = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const staffPage = Math.max(1, parseInt(searchParams.get('spage') || '1', 10) || 1);

  const setParams = (patch, { resetPage = true, replace = false } = {}) => {
    const { q, ...urlPatch } = patch;
    if (q !== undefined) setQuery(q);
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(urlPatch)) {
      if (v == null || v === '' || v === DEFAULTS[k]) next.delete(k); else next.set(k, v);
    }
    next.delete('q');
    if (resetPage) { next.delete('page'); next.delete('spage'); }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace });
  };

  // Search echoes keystrokes instantly; the URL/query commits after the debounce (§8),
  // so the committed `query` is already the debounced value the server sees.
  const [search, setSearch] = useState(query);
  const debounceRef = React.useRef(null);
  // Resync only on a change from elsewhere (clear filters, a migrated old link):
  // committing '' for a 1-letter entry must not erase the letter just typed.
  const committedRef = React.useRef(query);
  useEffect(() => {
    if (query !== committedRef.current) { committedRef.current = query; setSearch(query); }
  }, [query]);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    if (val === '') { committedRef.current = ''; setParams({ q: '' }, { replace: true }); return; }
    // Below two characters the previous term is dropped, not kept in force.
    const commit = val.trim().length < 2 ? '' : val;
    debounceRef.current = setTimeout(() => {
      committedRef.current = commit;
      setParams({ q: commit }, { replace: true });
    }, 300);
  };

  // Preset/custom → epoch-ms window. `presetRange` returns null for the unbounded
  // «همه» preset; the hook maps a null bound to 0, which is exactly what the screen
  // sent before this refactor — so the data-fetch semantics are unchanged. An
  // incomplete custom range falls back to the default month so the list never
  // flickers to an empty window mid-edit.
  const range = useMemo(() => {
    if (preset === 'custom') {
      return (custom.from && custom.to) ? customRange(custom.from, custom.to) : presetRange('month');
    }
    return presetRange(preset);
  }, [preset, custom.from, custom.to]);

  const [exporting, setExporting] = useState(false);
  const [showOthers, setShowOthers] = useState(false);   // disclosure = local UI state (§4.4)

  const clinics = useMyClinics();
  const { report, loading, error, reload, vars } = useAccounting({
    range, clinicId, itemType, settlement: tab, search: query, page, staffPage,
  });

  const rangeLabel = rangeLabelFor(preset, custom.from, custom.to, translatedPresets(t));

  // The server already split the lists: `mine` is the doctor's own work (paginated),
  // `others` the clinic staff's — on the doctor's invoices, but never in a total.
  const mine   = report.lines;
  const others = report.staffLines;
  const anyRows = report.linesTotal + report.staffLinesTotal > 0;

  // The KPI figures, one group per currency the counted lines are in — never one
  // figure across two. Each group carries `outstanding`, what the doctor is still
  // owed IN THAT CURRENCY: both terms come off the same server-filtered line set,
  // so it can never disagree with the two cards beside it. An empty report has no
  // currency of its own and states its zeros in the clinic's.
  const totals = reportTotals(report, displayCurrencyFor(clinics, clinicId));
  const mixed = totals.length > 1;

  // Any non-default filter (the month window included).
  const hasActiveFilters =
    tab !== DEFAULTS.settle || itemType !== DEFAULTS.type || clinicId !== DEFAULTS.clinic ||
    query !== DEFAULTS.q || preset !== DEFAULTS.preset;

  const clearFilters = () => {
    setSearch('');
    setParams({ settle: '', type: '', clinic: '', q: '', preset: '', from: '', to: '' });
  };

  // State contract (§2.4): skeleton on first load only; a refetch keeps the previous
  // rows visible, dimmed — never blanks to skeletons.
  const isEmpty  = !loading && !error && !mine.length;
  const skeleton = loading && !mine.length;
  const stale    = loading && mine.length > 0;
  const dataState = loading && !anyRows ? 'loading' : error ? 'error' : isEmpty ? 'empty' : 'content';

  // The CSV must keep meaning «everything in the filtered window», not «the rows that
  // happened to be loaded» — so it re-queries the same filters unpaginated.
  const onExport = async () => {
    setExporting(true);
    try {
      exportLinesCsv(await fetchAllReportLines(vars), rangeLabel, t, formatDate);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error(t('accounting.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const clinicOptions = clinics.map((c) => ({ value: c.id, label: c.name }));

  // Filtered-empty vs truly-empty (§2.4): a filtered-out list never claims the dataset
  // is empty. The default window is «این ماه», so the unfiltered copy speaks about the month.
  const emptyState = hasActiveFilters ? (
    <EmptyState
      icon={<SearchX size={52} color={C.textMuted} strokeWidth={1.5} aria-hidden="true" />}
      title={t('errors.empty.filters')}
      description={t('accounting.changeRangeOrFilters')}
      action={
        <Button variant="outline" size="sm" onClick={clearFilters} data-testid="accounting-empty-clear">
          <FilterX size={15} aria-hidden="true" />
          {t('encounters.clearFilters')}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<Inbox size={52} color={C.textMuted} strokeWidth={1.5} aria-hidden="true" />}
      title={t('accounting.noneInRange')}
      description={t('accounting.changeRangeHint')}
    />
  );

  return (
    <div className="h-full flex flex-col bg-background overflow-y-auto">

      {/* ① PageHeader — the shell owns the chrome; the page opens with the header (§2.1).
          The desktop sidebar is the way back, so no breadcrumb. */}
      <PageHeader
        title={t('nav.items.accounting')}
        sub={t('accounting.subtitle')}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={reload}
          aria-label={t('errors.reload')}
          title={t('errors.reload')}
          data-testid="accounting-header-reload"
        >
          <RotateCw size={18} aria-hidden="true" />
        </Button>
      </PageHeader>

      {/* ── Body (only the two sanctioned thresholds, §4.1) ── */}
      <div className="px-4 min-[769px]:px-8 py-6 flex flex-col gap-5">

        {error ? (
          <div data-testid="accounting-error">
            <ErrorState
              error={error}
              title={t('accounting.unavailable')}
              note={t('accounting.unavailableNote')}
              onRetry={reload}
            />
          </div>
        ) : (
        <div data-testid="accounting-content" data-state={dataState} aria-busy={loading || undefined} className="flex flex-col gap-5">

          {/* ③ KPI strip — every figure is scoped to the active filters. Dimmed while a
              new window loads, so the last filter's numbers never read as current (§2.4). */}
          {mixed ? (
            // Lines in more than one currency: one KPI strip per currency, each
            // headed by its currency's name. There is no combined figure — the
            // BFF sends none, and there is no exchange rate to make one.
            <div data-testid="accounting-kpis-by-currency" className={`flex flex-col gap-4 transition-opacity ${stale ? 'opacity-[0.45]' : ''}`}>
              {totals.map((group) => (
                <div key={group.currency} data-testid={`accounting-kpis-${group.currency}`}>
                  <div className="text-xs font-bold text-muted-foreground mb-2">{moneyUnit(group.currency)}</div>
                  <div className="grid grid-cols-1 min-[769px]:grid-cols-2 min-[1181px]:grid-cols-4 gap-3.5">
                    <KpiGroup group={group} skeleton={skeleton} suffix={`-${group.currency}`} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`grid grid-cols-1 min-[769px]:grid-cols-2 min-[1181px]:grid-cols-4 gap-3.5 transition-opacity ${stale ? 'opacity-[0.45]' : ''}`}>
              <KpiGroup group={totals[0]} skeleton={skeleton} />
            </div>
          )}

          {/* ④ Toolbar — one white card row: filters live here and only here (§9.2). */}
          <div
            data-testid="accounting-toolbar"
            className="bg-white rounded-2xl shadow-card px-4 py-3 flex flex-wrap items-center gap-2.5"
          >
            {/* Search leads the toolbar (§9.2 order: search · segmented · daterange · clear) */}
            <div className="flex-1 min-w-[12.5rem] max-w-[22rem]" data-testid="accounting-toolbar-search">
              <SearchInput
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t('accounting.searchPlaceholder')}
                clearLabel={t('common.actions.clearSearch')}
              />
            </div>

            <SegmentedControl items={TABS(t)} value={tab} onChange={(id) => setParams({ settle: id })} ariaLabel={t('accounting.settlementFilter')} testId="accounting-toolbar-settlement" />

            {clinics.length > 0 && (
              <div className="min-w-[10rem]" data-testid="accounting-toolbar-clinic">
                <Select value={clinicId} onChange={(e) => setParams({ clinic: e.target.value })} options={clinicOptions} placeholder={t('accounting.allClinics')} />
              </div>
            )}

            <div className="min-w-[9rem]" data-testid="accounting-toolbar-type">
              <Select value={itemType} onChange={(e) => setParams({ type: e.target.value })} options={TYPE_OPTIONS(t)} placeholder={t('accounting.allServices')} />
            </div>

            <div data-testid="accounting-toolbar-daterange">
              <DateRangeFilter
                presets={translatedPresets(t)}
                preset={preset}
                custom={custom}
                onPreset={(id) => setParams({ preset: id, from: '', to: '' })}
                onCustom={(from, to) => setParams({ preset: 'custom', from, to })}
              />
            </div>

            {/* Clear + export sit at the trailing (left, RTL) edge */}
            <span className="flex-1" />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="accounting-toolbar-clear">
                <FilterX size={15} aria-hidden="true" />
                {t('encounters.clearFilters')}
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={!anyRows || exporting}
              data-testid="accounting-toolbar-export"
            >
              <FileSpreadsheet size={15} aria-hidden="true" />
              {exporting ? t('common.state.preparing') : t('accounting.excel')}
            </Button>
          </div>

          {/* ⑤ Content — P2: main table (wide, right) + rail (320px, left). The rail is
              the grid's LAST column so it renders on the left in RTL, and stacks under
              the main column below 1180px (§2.2). */}
          <div className="grid grid-cols-1 min-[1181px]:grid-cols-[minmax(0,1fr)_20rem] gap-5 items-start">

            {/* Main — the line table */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden min-w-0">
              <div className="flex items-center justify-between px-5 pt-[1.125rem] pb-3.5 border-b border-border">
                <div className="text-[0.9375rem] font-extrabold text-foreground">
                  {t('accounting.lineItems')}
                  {report.linesTotal > 0 && (
                    <span className="text-xs font-semibold text-muted-foreground me-2">({formatNumber(report.linesTotal)} {t('common.units.itemsSuffix')}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{rangeLabel}</div>
              </div>

              {skeleton ? (
                <table className="w-full border-collapse">
                  <TableHead />
                  <tbody>{Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={COLUMNS(t).length} />)}</tbody>
                </table>
              ) : isEmpty ? (
                emptyState
              ) : (
                <div className={`overflow-x-auto transition-opacity ${stale ? 'opacity-[0.45] pointer-events-none' : ''}`}>
                  <table className="w-full border-collapse" data-testid="accounting-table">
                    <TableHead />
                    <tbody>{mine.map((l) => <LineRow key={l.id} line={l} />)}</tbody>
                  </table>
                </div>
              )}

              {/* ⑥ Pager */}
              {!skeleton && (
                <div data-testid="accounting-pager">
                  <Pager
                    page={page}
                    pageCount={Math.ceil(report.linesTotal / PAGE_SIZE)}
                    total={report.linesTotal}
                    pageSize={PAGE_SIZE}
                    onPage={(p) => setParams({ page: p > 1 ? String(p) : '' }, { resetPage: false })}
                  />
                </div>
              )}

              {/* Staff-performed lines: on the doctor's invoice, but not the doctor's work.
                  Shown, so nothing vanishes without a trace — but outside every total. */}
              {report.staffLinesTotal > 0 && (
                <div className="border-t border-border">
                  <button
                    onClick={() => setShowOthers((s) => !s)}
                    aria-expanded={showOthers}
                    data-testid="accounting-staff-toggle"
                    className="w-full px-5 py-3 bg-background text-muted-foreground text-xs font-bold text-start flex items-center gap-1.5 hover:text-foreground transition-colors"
                  >
                    {showOthers ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />}
                    {formatNumber(report.staffLinesTotal)} {t('accounting.performedByStaff')}
                    <span className="font-semibold text-muted-foreground">{t('accounting.outsideYourShare')}</span>
                  </button>
                  {showOthers && (
                    <div className={`transition-opacity ${stale ? 'opacity-[0.45] pointer-events-none' : ''}`}>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <TableHead />
                          <tbody>{others.map((l) => <LineRow key={l.id} line={l} />)}</tbody>
                        </table>
                      </div>
                      <Pager
                        page={staffPage}
                        pageCount={Math.ceil(report.staffLinesTotal / PAGE_SIZE)}
                        total={report.staffLinesTotal}
                        pageSize={PAGE_SIZE}
                        onPage={(p) => setParams({ spage: p > 1 ? String(p) : '' }, { resetPage: false })}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Rail — breakdowns (last grid column → left in RTL) */}
            <div className={`flex flex-col gap-5 transition-opacity ${stale ? 'opacity-[0.45]' : ''}`}>
              <ClinicBreakdown clinics={report.clinics} />
              <TypeBreakdown types={report.types} />
            </div>
          </div>

        </div>
        )}
      </div>
    </div>
  );
};

export default AccountingWeb;
